import { useState, useEffect } from "react";
import config from "../../config/config";
import { Doughnut, Pie, Bar } from "react-chartjs-2";
import "./anlatic.css"
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { refreshDashboardData } from "../../observers/dataObserver";
import { userSession } from "../../observers/userObserver";

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend
);

const COLORS = {
  active: "#006565", 
  inactive: "#00b3b3",
  billable: "#006565",
  nonBillable: "#bcffff",
};

const defaultDashboardStats = {
  completedRequests: 0,
  nonCompletedRequests: 0,
  activeRequests: 0,
  activeCustomers: 0,
  inactiveRequests: 0,
  activeUsers: 0,
  totalUsers: 100,
  billableRemaining: 0,
  billableUsed: 0,
  billingUsageBreakdown: {},
  billingByCustomer: [],
};

const chartOptions = {
  plugins: { legend: { display: true } },
  maintainAspectRatio: false,
};

const createActiveUserChartData = (activeUsers, totalUsers) => ({
  labels: ["Active", "Inactive/Pending"],
  datasets: [
    {
      data: [activeUsers, totalUsers - activeUsers],
      backgroundColor: [COLORS.active, COLORS.inactive],
    },
  ],
});

const createBillableChartData = (billableRemaining, billableUsed) => ({
  labels: ["Billable", "Used"],
  datasets: [
    {
      data: [billableRemaining, billableUsed],
      backgroundColor: [COLORS.billable, COLORS.nonBillable],
    },
  ],
});

const createStackedBarData = (usageData) => {
  const labels = Object.keys(usageData);
  const usedData = labels.map((key) => usageData[key].used);
  const remainingData = labels.map((key) =>
    Math.max(usageData[key].allowed - usageData[key].used, 0)
  );

  return {
    labels,
    datasets: [
      {
        label: "Used",
        data: usedData,
        backgroundColor: COLORS.billable,
      },
      {
        label: "Remaining",
        data: remainingData,
        backgroundColor: COLORS.nonBillable,
      },
    ],
  };
};

const createCustomerUsageChart = (customerUsageList) => {
  const labels = customerUsageList.map(item => item.customer_name);
  const usedData = customerUsageList.map(item => item.billing_usage["Create Request"].used);
  const remainingData = customerUsageList.map(item => {
    const usage = item.billing_usage["Create Request"];
    return Math.max(usage.allowed - usage.used, 0);
  });

  return {
    labels,
    datasets: [
      {
        label: "Used",
        data: usedData,
        backgroundColor: COLORS.billable,
         borderRadius: 10, // 👈 Rounded corners
      barThickness: 15, // (optional) make bars thinner
      },
      {
        label: "Remaining",
        data: remainingData,
        backgroundColor: COLORS.nonBillable,
         borderRadius: 10, // 👈 Rounded corners
      barThickness: 15, // (optional) make bars thinner
      },
    ],
  };
};

const AnalyticsComponent = () => {
  const [dashboardStats, setDashboardStats] = useState(defaultDashboardStats);

  const {
    activeRequests,
    inactiveRequests,
    activeCustomers,
    activeUsers,
    totalUsers,
    billableRemaining,
    billableUsed,
    billingUsageBreakdown,
    billingByCustomer
  } = dashboardStats;

  const fetchDashboardData = async () => {
    try {
      const token = userSession.getToken();
      const userEmail = userSession.getUserEmail();

      if (!token) {
        await userSession.refreshToken();
        if (!userSession.getToken()) {
          console.warn("⚠️ Permanent auth failure");
          return;
        }
      }

      const dashboardResponse = await fetch(`${config["backend-url"]}/COD_dashboard_data`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const dashboardData = await dashboardResponse.json();

      let billingUsageBreakdown = {};
      let billingByCustomer = [];

      const usageResponse = await fetch(`${config["backend-url"]}/get_billing_usage`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const usageData = await usageResponse.json();

      if (Array.isArray(usageData)) {
        billingByCustomer = usageData;
      } else if (usageData.billing_usage) {
        billingUsageBreakdown = usageData.billing_usage;
      }

      if (dashboardResponse.ok) {
        const charts = dashboardData.charts || {};

        setDashboardStats((prev) => ({
          ...prev,
          completedRequests: charts.completedRequests || 0,
          nonCompletedRequests: charts.nonCompletedRequests || 0,
          activeRequests: charts.inProgressRequests || 0,
          inactiveRequests: charts.inactiveRequests || 0,
          activeUsers: charts.ActiveUsers || 0,
          activeCustomers: charts.activeCustomers || 0,
          billableRemaining: charts.billableRemaining || 0,
          billableUsed: charts.billableUsed || 0,
          totalUsers: charts.totalUsers || 0,
          totalCustomers: charts.totalCustomers || 0,
          billingUsageBreakdown,
          billingByCustomer
        }));
      } else {
        console.error("❌ Failed to load dashboard data:", dashboardData.error);
      }
    } catch (error) {
      console.error("❌ Network or server error:", error);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    refreshDashboardData.subscribe((value) => {
      if (value === "refresh") {
        fetchDashboardData();
      }
    });
  }, []);

  return (
    <>
  <div className="dashboard-header">
    <h1 className="welcomeText">COnD Administration Interface</h1>
  </div>

  <div className="dashboard-sections">
    {/* KPI Column - Aligned bottom */}
    <div className="dashboard-section kpi-column">
      <div className="kpi-wrapper">
        {[
          { label: "Active Customers", value: activeCustomers },
          { label: "Active Requests", value: activeRequests },
          { label: "Active Users", value: activeUsers },
        ].map((kpi, i) => (
          <div className="kpi-box" key={i}>
            <h4>{kpi.label}</h4>
            <p className="kpi-value">{kpi.value}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Pie Chart */}
    <div className="dashboard-section pie-column">
      <h4>Active Users V/S Inactive Users</h4>
      <p className="kpi-value">{activeUsers}</p>
      <div className="chart-container">
        <Pie data={createActiveUserChartData(activeUsers, totalUsers)} options={chartOptions} />
      </div>
    </div>

    {/* Bar Chart */}
    <div className="dashboard-section bar-column">
      <h4>"Create Request" Usage by Customer</h4>
      <div className="chart-container">
        <Bar
          data={createCustomerUsageChart(billingByCustomer)}
          options={{
            ...chartOptions,
            responsive: true,
            plugins: { legend: { position: "top" } },
            scales: { x: { stacked: true }, y: { stacked: true } },
          }}
        />
      </div>
    </div>
  </div>
</>


  );
};

export default AnalyticsComponent;
